import type { LoaderFunctionArgs } from '~/types/route-args';
import type { MetaDescriptor } from 'react-router';
import { redirect } from 'react-router';
import invariant from 'tiny-invariant';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { createOrganisationSchema } from '~/modules/seo/schema/organisation-schema';
import { createWebsiteSchema } from '~/modules/seo/schema/website-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getMetaBaseUrl } from '~/modules/seo/util/create-meta-base-url';
import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { APHandle } from '@modules/root';
import { buildLoggedOutHomepageData } from '@modules-rocco/home/api/build-logged-out-homepage-data.server';
import { getMatchedStyleImages as fetchGetMatchedStyleImages } from '~/modules/get-matched/api/get-matched-style-images.server';

import { HomePage } from '@ui-rocco/pages/home-page/HomePage';
import { useHomePageTracking } from '~/ui-rocco/hooks/use-home-page-tracking';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

/**
 * The homepage uses the unified header. The tall hero-overlay layout
 * (`showLogomark`) renders the large logomark + vertical nav stack over the
 * dark hero; `pageTheme: 'transparent'` already maps to the `transparent`
 * colour variant in AppPage. No tagline/CTA overrides — the header's rich
 * default tagline and standard Log In / Sign Up cluster match the design.
 */
export const handle: APHandle = {
    header: {
        headerVariant: 'transparent',
        showLogomark: true,
    },
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { matches, data } = props;

        const title = createMetaTitle(
            props,
            [data?.localisation?.Title],
            false,
        );
        const description = createMetaDescription([
            data?.localisation?.Description,
        ]);

        const { rootData } = getRouteData(matches);

        const baseUrl = getMetaBaseUrl(props);
        invariant(
            rootData?.localisation.country,
            'index.ts meta(): country is required',
        );

        const organisationSchema = createOrganisationSchema(props);
        const websiteSchema = createWebsiteSchema(baseUrl);

        const ldSchema = createLDSchema([
            organisationSchema ?? [],
            websiteSchema ?? [],
        ]);

        // Temporarily remove preloading until structure is confirmed
        const preloadImageLinks: MetaDescriptor[] = [];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            // The NZ/AU/US market homepages are language-identical equivalents
            // of one another, so advertise the full market cluster + x-default
            // rather than a self-only canonical. The same code runs on each
            // apex host, so the cluster is reciprocal; this lets Google serve
            // the US homepage to US users instead of consolidating brand
            // queries onto the established NZ/AU sites.
            ...getCanonicalHreflang(props, { forceAllRegions: true }),
            ...ldSchema,
            ...preloadImageLinks,
        ];
    },
);

export const loader = async (args: LoaderFunctionArgs) => {
    const { context } = args;
    const isLoggedIn = context.user.__typename === 'Me';

    if (isLoggedIn) {
        return redirect('/app');
    }

    const localisation = await getPageLocalisation(args);
    const [roccoHomePageData, getMatchedStyleImages] = await Promise.all([
        buildLoggedOutHomepageData(args),
        fetchGetMatchedStyleImages(context),
    ]);

    return {
        deviceType: context.device.type,
        localisation,
        ...roccoHomePageData,
        getMatchedStyleImages,
        isLoggedIn,
        pageTheme: 'transparent',
    };
};

const HomeRoute = () => {
    useHomePageTracking();

    const { setCategory } = useCurrentCategory();

    React.useEffect(() => {
        setCategory('All');
        return () => setCategory(null);
    }, [setCategory]);

    return <HomePage />;
};

export default HomeRoute;
